import React, { useEffect, useState } from 'react'
import { css } from 'glamor'
import { useLocation } from 'react-router-dom'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import * as theme from '../../theme'
import { CardContainer } from '../../components/Container'
import { H1, H2 } from '../../components/Typography'
import { Table, TBody, THead } from '../../components/Table'
import { CellPill } from '../../components/StatusPill'
import { Button, HollowButton } from '../../components/Button'
import { GraphqlErrorMessage } from '../../components/ErrorMessage'
import { withInstallation } from '../../components/InstallationContext'
import { isPinRecoveryEnabled } from '../../lib/pinRecoveryFeature'
import { fmtDate } from '../../lib/misc'
import drivoApollo from '../../lib/drivoApollo'
import {
  pinRecoveryLookup,
  resendPinCredential,
  retryPinProvisioning,
  revokePinCredential,
} from './queries'

const DATE_FORMAT = 'EEE, d MMM yyyy HH:mm'

const searchRow = css({
  display: 'flex',
  gap: 12,
  alignItems: 'center',
  marginBottom: 16,
})

const searchInput = css({
  flex: 1,
  maxWidth: 400,
  padding: '8px 12px',
  border: `1px solid ${theme.gray30}`,
  borderRadius: 6,
  fontSize: 14,
  outline: 'none',
  ':focus': {
    borderColor: theme.blue,
  },
})

const noteStyle = css({
  color: theme.gray60,
  fontSize: 13,
  lineHeight: 1.5,
  marginBottom: 20,
  maxWidth: 720,
})

const rowStyle = css({
  '> td': {
    verticalAlign: 'middle !important',
    height: '50px !important',
    borderBottom: `1px solid ${theme.gray10} !important`,
    textAlign: 'center !important',
    fontSize: '12px !important',
    padding: '8px 8px !important',
  },
})

const headStyle = css({
  '> th': {
    textAlign: 'center !important',
    fontSize: '11px !important',
    fontWeight: 'bold !important',
    color: `${theme.gray50} !important`,
    textTransform: 'uppercase',
    padding: '12px 8px !important',
    borderBottom: `2px solid ${theme.gray30} !important`,
    whiteSpace: 'nowrap',
  },
})

const pinStyle = css({
  fontFamily: 'monospace',
  fontSize: 18,
  fontWeight: 'bold',
  letterSpacing: 3,
})

const actionsCell = css({
  display: 'flex',
  gap: 6,
  justifyContent: 'center',
})

const reasonInput = css({
  width: '100%',
  minHeight: 70,
  padding: '8px 12px',
  border: `1px solid ${theme.gray30}`,
  borderRadius: 6,
  fontSize: 14,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
})

const STATUS_COLORS = {
  provisioned: theme.green,
  pending: theme.orange,
  failed: theme.red,
  revoked: theme.gray50,
  expired: theme.gray40,
}

const PIN_PLACEHOLDER = {
  pending: 'Not ready',
  failed: 'Not issued',
}

function pinPlaceholder(row) {
  /* The API holds back a booking's PIN until its window opens. */
  if (row.status === 'provisioned' && new Date(row.validFrom) > new Date()) return 'Not valid yet'
  return PIN_PLACEHOLDER[row.status] || '—'
}

export const ACTIONS = {
  revoke: {
    label: 'Revoke',
    title: 'Revoke PIN',
    confirm: 'Revoke PIN',
    flavour: 'danger',
    mutation: revokePinCredential,
    field: 'revokePinCredential',
    reasonRequired: true,
    intro: () =>
      'Use this when the PIN has been shared or seen by someone else. It stops opening the door and no new PIN is issued. Give the guest assisted access.',
  },
  retry: {
    label: 'Retry',
    title: 'Retry PIN',
    confirm: 'Retry',
    flavour: 'primary',
    mutation: retryPinProvisioning,
    field: 'retryPinProvisioning',
    reasonRequired: false,
    intro: () => 'Try issuing this PIN again. Give the guest assisted access until it shows a PIN.',
  },
  resend: {
    label: 'Resend',
    title: 'Resend PIN',
    confirm: 'Send email',
    flavour: 'primary',
    mutation: resendPinCredential,
    field: 'resendPinCredential',
    reasonRequired: false,
    intro: (row) =>
      `Check the guest's details first. The PIN goes in the booking confirmation email to the email on the booking only: ${row.guestEmail}.`,
  },
}

/* Updates a row from an action's result so the table is right without a new, audited search. */
export function afterAction(row, action, result) {
  const live = result.status === 'provisioned'
  const revoking = action === 'revoke' && result.outcome !== 'refused'
  return {
    ...row,
    status: result.status,
    /* After a revoke the PIN may still work for a while, but it must not be read out again. */
    pin: live && !revoking ? row.pin : null,
    canRevoke: row.canRevoke && result.status !== 'revoked',
    canRetry: action === 'retry' ? false : row.canRetry && !revoking,
    canResend: row.canResend && live && !revoking,
  }
}

const DIALOG_TITLE_ID = 'pin-recovery-dialog-title'

export function ActionDialog({ action, row, onClose, onDone }) {
  const config = ACTIONS[action]
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)
  const missingReason = config.reasonRequired && !reason.trim()

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const { data } = await drivoApollo.mutate({
        mutation: config.mutation,
        variables: { credentialId: row.id, reason: reason.trim() || null },
      })
      const outcome = data[config.field]
      setResult(outcome)
      onDone(row.id, action, outcome)
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby={DIALOG_TITLE_ID}>
      <DialogContent>
        <H2 id={DIALOG_TITLE_ID} spaceAbove="none">{config.title}</H2>
        <p {...noteStyle}>{config.intro(row)}</p>
        {result ? (
          <p style={{ fontSize: 14, fontWeight: 'bold', color: result.outcome === 'refused' ? theme.red : theme.gray60 }}>
            {result.message}
          </p>
        ) : (
          <>
            {error && <GraphqlErrorMessage error={error} />}
            <label style={{ fontSize: 13, color: theme.gray60 }}>
              Reason{config.reasonRequired ? '' : ' (optional)'}
              <textarea
                {...reasonInput}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={busy}
              />
            </label>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button spaceBelow="none" onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button spaceBelow="none" mode="naked" flavour="gray" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button spaceBelow="none" flavour={config.flavour} onClick={submit} disabled={busy || missingReason}>
              {busy ? 'Working…' : config.confirm}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

function CredentialRow({ row, timeZone, onAction }) {
  const isWalkup = row.kind === 'walkup'
  return (
    <tr {...rowStyle}>
      <td>{isWalkup ? 'Walk-up' : 'Booking'}</td>
      <td style={{ fontWeight: 'bold' }}>{row.plate || '—'}</td>
      <td>
        {isWalkup
          ? `Entered ${fmtDate(row.enteredAt, timeZone, DATE_FORMAT)}`
          : `${row.bookingLabel || row.bookingId} (${row.bookingStatus})`}
      </td>
      <td>{row.guestName || '—'}</td>
      <td title={row.status === 'failed' ? row.lastError || '' : ''}>
        <CellPill color={STATUS_COLORS[row.status] || theme.gray50} width={80}>
          {row.status}
        </CellPill>
      </td>
      <td>
        {row.pin ? <span {...pinStyle}>{row.pin}</span> : pinPlaceholder(row)}
      </td>
      <td>{fmtDate(row.validTo, timeZone, DATE_FORMAT)}</td>
      <td>
        <div {...actionsCell}>
          {row.canResend && (
            <HollowButton size="small" spaceBelow="none" onClick={() => onAction('resend', row)}>
              {ACTIONS.resend.label}
            </HollowButton>
          )}
          {row.canRetry && (
            <HollowButton size="small" spaceBelow="none" onClick={() => onAction('retry', row)}>
              {ACTIONS.retry.label}
            </HollowButton>
          )}
          {row.canRevoke && (
            <HollowButton size="small" spaceBelow="none" flavour="danger" onClick={() => onAction('revoke', row)}>
              {ACTIONS.revoke.label}
            </HollowButton>
          )}
        </div>
      </td>
    </tr>
  )
}

export function PinRecoveryPage({ installation }) {
  const location = useLocation()
  const initialSearch = new URLSearchParams(location.search).get('search') || ''
  const [search, setSearch] = useState(initialSearch)
  const [searched, setSearched] = useState('')
  const [rows, setRows] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [dialog, setDialog] = useState(null)

  /* Every lookup is audited, so search only when asked, never while typing. */
  async function runLookup(value) {
    const term = value.trim()
    if (!term) return
    setLoading(true)
    setError(null)
    /* Drop the old rows, so none of their actions can run while this lookup is pending. */
    setRows(null)
    try {
      const { data } = await drivoApollo.query({
        query: pinRecoveryLookup,
        variables: { installationId: installation.id, search: term },
        fetchPolicy: 'no-cache',
      })
      setRows(data.pinRecoveryLookup)
      setSearched(term)
    } catch (e) {
      setError(e)
      setRows(null)
    } finally {
      setLoading(false)
    }
  }

  /* Opened from a booking with ?search=<booking id>. */
  useEffect(() => {
    if (initialSearch) runLookup(initialSearch)
  }, [])

  function onDone(rowId, action, result) {
    setRows((current) => current.map((row) => (row.id === rowId ? afterAction(row, action, result) : row)))
  }

  return (
    <CardContainer styles={{ padding: 30 }}>
      <H1>PIN Recovery</H1>
      <p {...noteStyle}>
        Search by plate or booking id. For a walk-up guest, ask for their plate and check it matches before giving
        the PIN. If it does not match, or the PIN is failed or expired, give assisted access. Every search and
        action is recorded.
      </p>

      <form
        {...searchRow}
        onSubmit={(e) => {
          e.preventDefault()
          runLookup(search)
        }}
      >
        <input
          {...searchInput}
          type="text"
          placeholder="Plate or booking id"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button type="submit" spaceBelow="none" disabled={loading || !search.trim()}>
          {loading ? 'Searching…' : 'Search'}
        </Button>
      </form>

      {error && <GraphqlErrorMessage error={error} />}

      {rows && rows.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: theme.gray40, fontSize: 14 }}>
          No PINs found for "{searched}".
        </div>
      )}

      {rows && rows.length > 0 && (
        <Table>
          <THead>
            <tr {...headStyle}>
              <th>Type</th>
              <th>Plate</th>
              <th>Entry / Booking</th>
              <th>Guest</th>
              <th>Status</th>
              <th>PIN</th>
              <th>Valid until</th>
              <th />
            </tr>
          </THead>
          <TBody>
            {rows.map((row) => (
              <CredentialRow
                key={row.id}
                row={row}
                timeZone={installation.timeZoneName}
                onAction={(action, target) => setDialog({ action, row: target })}
              />
            ))}
          </TBody>
        </Table>
      )}

      {dialog && (
        <ActionDialog
          action={dialog.action}
          row={dialog.row}
          onClose={() => setDialog(null)}
          onDone={onDone}
        />
      )}
    </CardContainer>
  )
}

/* The sidebar only links here at pedestrian-PIN hotels; a typed URL elsewhere renders nothing. */
export function PinRecoveryRoute({ installation, facility }) {
  if (!isPinRecoveryEnabled(facility)) return null

  return <PinRecoveryPage installation={installation} />
}

export default withInstallation(PinRecoveryRoute)
